import React, { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { Hub } from 'aws-amplify/utils';
import { AppProvider } from './context/AppContext';
import PrivateRoute from './components/auth/PrivateRoute';
import PublicRoute from './components/auth/PublicRoute';
import Layout from './components/layout/Layout';
import LoadingSpinner from './components/layout/LoadingSpinner';
import { useAppStore } from './stores/useAppStore';
import { useAuthStore } from './stores/useAuthStore';
import useNativeSetup from './hooks/useNativeSetup';
import './App.css';

const Home = lazy(() => import('./pages/Home'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const BlockList = lazy(() => import('./pages/BlockList'));
const Settings = lazy(() => import('./pages/Settings'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const OAuthCallbackPage = lazy(() => import('./pages/OAuthCallbackPage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));

function NightModeSync() {
  const nightMode = useAppStore((s) => s.nightMode);
  const setNightMode = useAppStore((s) => s.setNightMode);

  useEffect(() => {
    document.documentElement.classList.toggle('dark', nightMode);
    document.documentElement.setAttribute('data-theme', nightMode ? 'dark' : 'light');
  }, [nightMode]);

  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e) => setNightMode(e.matches);
    if (mq.addEventListener) mq.addEventListener('change', handler);
    else mq.addListener(handler);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', handler);
      else mq.removeListener(handler);
    };
  }, [setNightMode]);

  return null;
}

function AuthEventListener() {
  const clearAuth = useAuthStore((s) => s.clearAuth);
  useEffect(() => {
    const unsubscribe = Hub.listen('auth', ({ payload }) => {
      if (payload.event === 'signedOut') clearAuth();
    });
    return () => unsubscribe();
  }, [clearAuth]);
  return null;
}

function NativeSetup() {
  useNativeSetup();
  return null;
}

function App() {
  return (
    <AppProvider>
      <NightModeSync />
      <NativeSetup />
      <AuthEventListener />
      <BrowserRouter>
        <Suspense fallback={<LoadingSpinner size="lg" />}>
          <Routes>
            <Route element={<PublicRoute />}>
              <Route path="/login" element={<LoginPage />} />
            </Route>

            <Route element={<PrivateRoute />}>
              <Route element={<Layout />}>
                <Route path="/" element={<Home />} />
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/blocklist" element={<BlockList />} />
                <Route path="/settings" element={<Settings />} />
              </Route>
            </Route>

            <Route path="/auth/callback" element={<OAuthCallbackPage />} />
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </AppProvider>
  );
}

export default App;
